import { clsx } from "clsx";
import { HTMLAttributes, ReactNode, useId, useState } from "react";
import { Icon, type IconName } from "../Icons";
import { Heading } from "../Typography";
import "./InfoBanner.css";

export type InfoBannerVariant =
  | "default"
  | "sky"
  | "success"
  | "warning"
  | "error";

export interface InfoBannerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "content"> {
  content: string;
  className?: string;
  /** Optional action element (e.g. a Button) rendered on the right side of the banner. */
  action?: ReactNode;
  /**
   * Background / text / icon color variant.
   * Maps to the figma "Banner message" component set.
   * @default "default"
   */
  variant?: InfoBannerVariant;
  /**
   * Optional ARIA role override. When omitted, the component picks a
   * sensible default per variant ("alert" for error, "status" for
   * warning/success, none for informational variants).
   */
  role?: string;
  /**
   * Detail body shown under `content`, hidden while the banner is collapsed.
   *
   * Passing it turns the banner into a summary the reader can fold away: a
   * findings list that would otherwise dominate the page stays one click from
   * view, and `content` alone keeps the finding on screen. Without it the
   * banner has nothing to collapse and no toggle is rendered.
   */
  details?: ReactNode;
  /**
   * Secondary text under `content`, always shown.
   *
   * For a banner that needs a sentence of explanation under its heading but
   * has nothing worth folding away — `details` would add a toggle that only
   * hides the one line telling the reader what to do.
   */
  description?: ReactNode;
  /**
   * Draws the banner's outline in its own palette. Opt-in: the banners
   * already on screen were designed without one.
   */
  bordered?: boolean;
  /**
   * Renders a close control at the end of the heading row, tinted to the
   * banner's palette like the collapse toggle. The caller decides what closing
   * means — the banner keeps no state of its own about it.
   */
  onDismiss?: () => void;
  /** Accessible name for the close control. @default "Dismiss" */
  dismissLabel?: string;
  /**
   * Initial collapsed state for an uncontrolled banner.
   * @default false
   */
  defaultCollapsed?: boolean;
  /**
   * Collapsed state, for a caller that owns it — pass it alongside
   * `onCollapsedChange` to persist the choice across remounts. Leave it
   * undefined to let the banner keep its own state.
   */
  collapsed?: boolean;
  /** Fires with the state the banner is moving to, controlled or not. */
  onCollapsedChange?: (collapsed: boolean) => void;
}

// Per-variant icon. `sky` is intentionally icon-less in the figma spec.
const VARIANT_ICON: Record<InfoBannerVariant, IconName | undefined> = {
  default: "functional-info-reversed",
  sky: undefined,
  success: "functional-check-green-reversed",
  warning: "functional-warning-reversed",
  error: "functional-failed-reversed",
};

// Default ARIA role per variant for dynamically-rendered banners. Caller
// can override via the `role` prop.
const VARIANT_DEFAULT_ROLE: Record<InfoBannerVariant, string | undefined> = {
  default: undefined,
  sky: undefined,
  success: "status",
  warning: "status",
  error: "alert",
};

export const InfoBanner = ({
  content,
  className,
  action,
  variant = "default",
  role,
  details,
  description,
  bordered = false,
  onDismiss,
  dismissLabel = "Dismiss",
  defaultCollapsed = false,
  collapsed,
  onCollapsedChange,
  ...rest
}: InfoBannerProps) => {
  const iconName = VARIANT_ICON[variant];
  const resolvedRole = role ?? VARIANT_DEFAULT_ROLE[variant];
  const detailsId = useId();

  const [selfCollapsed, setSelfCollapsed] = useState(defaultCollapsed);
  // `collapsed` present means the caller owns the state; the internal one is
  // then only a fallback that never gets read.
  const isControlled = collapsed !== undefined;
  const isCollapsed = isControlled ? collapsed : selfCollapsed;

  // A banner with nothing to hide must not offer a toggle — collapsing it
  // would leave the reader looking at the same thing.
  const isCollapsible = Boolean(details);
  const showDetails = isCollapsible && !isCollapsed;

  const toggle = () => {
    const next = !isCollapsed;
    if (!isControlled) {
      setSelfCollapsed(next);
    }
    onCollapsedChange?.(next);
  };

  return (
    <div
      className={clsx(
        "indigo-info-banner",
        variant !== "default" && `indigo-info-banner--${variant}`,
        // Only an expanded banner switches to top alignment, so every
        // single-line banner renders exactly as it did before.
        (showDetails || Boolean(description)) && "indigo-info-banner--expanded",
        bordered && "indigo-info-banner--bordered",
        className,
      )}
      role={resolvedRole}
      {...rest}
    >
      {iconName && (
        <span className="indigo-info-banner__variant-icon" aria-hidden="true">
          <Icon name={iconName} size="xs" />
        </span>
      )}
      {/* The body column is what indents the details under the heading text
          rather than under the icon, without either side hardcoding the
          icon's width. */}
      <div className="indigo-info-banner__body">
        <div className="indigo-info-banner__row">
          <Heading
            level={variant === "sky" ? 4 : 5}
            className="indigo-info-banner__text"
          >
            {content}
          </Heading>
          {action && <div className="indigo-info-banner__action">{action}</div>}
          {isCollapsible && (
            <button
              type="button"
              className="indigo-info-banner__toggle"
              onClick={toggle}
              aria-expanded={showDetails}
              aria-controls={detailsId}
              aria-label={isCollapsed ? "Show details" : "Hide details"}
            >
              <Icon name={isCollapsed ? "expand" : "collapse"} size="xs" />
            </button>
          )}
          {onDismiss && (
            <button
              type="button"
              className="indigo-info-banner__dismiss"
              onClick={onDismiss}
              aria-label={dismissLabel}
            >
              <Icon name="circle-close-filled" size="s" />
            </button>
          )}
        </div>
        {description && (
          <div className="indigo-info-banner__details">{description}</div>
        )}
        {/* Kept mounted while collapsed so `aria-controls` always resolves to
            a real element, which is what lets a screen reader announce the
            toggle's expanded state against something. */}
        {isCollapsible && (
          <div
            id={detailsId}
            className="indigo-info-banner__details"
            hidden={!showDetails}
          >
            {details}
          </div>
        )}
      </div>
    </div>
  );
};

InfoBanner.displayName = "InfoBanner";
